import { IconDownload, IconFileInvoice, IconRefresh, IconRotateClockwise } from "@tabler/icons-react";
import { Badge, Button, Group, Stack, Text, ThemeIcon, Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui";
import { useUserPermisos } from "@/lib/auth-store";
import { PERMISOS } from "@/lib/permisos";
import type { PolizaApi } from "@/lib/types";
import { useDescargarPolizaTxt, useRegenerarPoliza, useReintentarDepositoPoliza } from "../hooks/use-polizas-descargas";
import { formatearMonto } from "@/shared/format/money";

/**
 * Una póliza individual · tarjeta con UUID + metadata clave (org, prov,
 * total) y botón de descarga TXT si ya fue depositada en SFTP.
 */
export function PolizaItem({
  poliza,
  solicitudId,
  puedeDescargar = false,
}: {
  poliza: PolizaApi;
  solicitudId: string;
  /** Solo Auditor/Administrador ven el botón de descarga del TXT técnico. */
  puedeDescargar?: boolean;
}) {
  const descargar = useDescargarPolizaTxt(solicitudId);
  const reintentar = useReintentarDepositoPoliza(solicitudId);
  const regenerar = useRegenerarPoliza(solicitudId);
  const permisos = useUserPermisos();
  const depositada = poliza.estado === "DepositadaEnSftp";
  const conError = poliza.estado === "ErrorEnDeposito";
  // Hotspot 53 · solo fallas de depósito (nunca bloqueo fiscal) y con el permiso del usuario.
  const puedeReintentar =
    conError && poliza.puedeReintentarDeposito === true && permisos.includes(PERMISOS.contabilizacionReintentar);
  // Hotspots 53/109 · error sin TXT guardado o por datos inválidos (nunca bloqueo fiscal) · mismo permiso.
  const puedeRegenerar =
    conError && poliza.puedeRegenerarse === true && permisos.includes(PERMISOS.contabilizacionReintentar);

  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-lg border border-border p-2.5">
      <Group justify="space-between" wrap="nowrap" align="center">
        <Group gap={6} wrap="nowrap" className="min-w-0">
          <ThemeIcon variant="filled" color="multiva" radius="md" size="sm" className="shrink-0">
            <IconFileInvoice size={14} stroke={1.6} />
          </ThemeIcon>
          <Text fw={600} size="sm">
            CFDI {poliza.numeroCfdi}
          </Text>
        </Group>
        <Badge size="xs" color={depositada ? "exito" : conError ? "peligro" : "slate"}>
          {depositada ? "Depositada" : conError ? "Error" : "Generada"}
        </Badge>
      </Group>

      <Tooltip>
        <TooltipTrigger asChild>
          <code className="block overflow-hidden text-ellipsis whitespace-nowrap rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
            {poliza.cfdiUuid}
          </code>
        </TooltipTrigger>
        <TooltipContent>{poliza.cfdiUuid}</TooltipContent>
      </Tooltip>

      <Stack gap={2}>
        <MetadataRow label="Org" valor={`${poliza.numeroSociedad} · ${poliza.organizacionNombre}`} />
        <MetadataRow label="Prov" valor={`#${poliza.numeroProveedor}`} />
        <Group gap={4} wrap="nowrap" justify="space-between">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} className="tracking-wide" style={{ fontSize: 9 }}>
            Total{poliza.moneda ? ` · ${poliza.moneda}` : ""}
          </Text>
          <Text size="sm" fw={700}>
            {formatearMonto(poliza.totalDebe, poliza.moneda, { minimumFractionDigits: 2 })}
          </Text>
        </Group>
      </Stack>

      {poliza.nombreArchivoSftp ? (
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <Text size="xs" c="dimmed" ff="mono" truncate style={{ fontSize: 10 }}>
                {poliza.nombreArchivoSftp}
              </Text>
            </TooltipTrigger>
            <TooltipContent>{poliza.nombreArchivoSftp}</TooltipContent>
          </Tooltip>
          {puedeDescargar ? (
            <Group justify="flex-end">
              <Button
                variant="default"
                size="xs"
                leftSection={<IconDownload size={12} />}
                loading={descargar.isPending}
                onClick={() => descargar.mutate({ polizaId: poliza.id, nombre: poliza.nombreArchivoSftp! })}
              >
                TXT
              </Button>
            </Group>
          ) : null}
        </>
      ) : null}

      {conError && poliza.errorDeposito ? (
        <Text size="xs" c="peligro-800">
          {poliza.errorDeposito}
        </Text>
      ) : null}
      {conError && poliza.proximoReintentoAt ? (
        <Text size="xs" c="dimmed">
          Reintento automático: {new Date(poliza.proximoReintentoAt).toLocaleString("es-MX")}
        </Text>
      ) : null}
      {puedeReintentar || puedeRegenerar ? (
        <Group justify="flex-end" gap={6}>
          {puedeReintentar ? (
            <Button
              variant="default"
              size="xs"
              leftSection={<IconRefresh size={12} />}
              loading={reintentar.isPending}
              disabled={regenerar.isPending}
              onClick={() => reintentar.mutate(poliza.id)}
            >
              Reintentar depósito
            </Button>
          ) : null}
          {puedeRegenerar ? (
            <Button
              variant="default"
              size="xs"
              leftSection={<IconRotateClockwise size={12} />}
              loading={regenerar.isPending}
              disabled={reintentar.isPending}
              onClick={() => regenerar.mutate(poliza.id)}
            >
              Regenerar póliza
            </Button>
          ) : null}
        </Group>
      ) : null}
    </div>
  );
}

function MetadataRow({ label, valor }: { label: string; valor: string }) {
  return (
    <Group gap={4} wrap="nowrap">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} className="shrink-0 tracking-wide" style={{ fontSize: 9 }}>
        {label}
      </Text>
      <Text size="xs" truncate>
        {valor}
      </Text>
    </Group>
  );
}
